import { MMButton } from '@components'
import cn from 'classnames'
import { globalSettings, TwoFactorTypes, useChallengeRequest } from 'mmfintech-checkout-commons'
import { checkSingleValue, tr, useValidUntilTimer } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useEffect, useMemo, useState } from 'react'
import { getOptTitle } from './WalletOtp.helper'

import './WalletOtp.scss'

type WalletOtpProps = {
  onSuccess: (response: unknown) => void
  onCancel?: () => void
  wallet?: boolean
}

const WalletOtp = ({ onSuccess, onCancel, wallet }: WalletOtpProps) => {
  const [attempts, setAttempts] = useState(0)
  const [verificationCode, setVerificationCode] = useState('')

  const timer = useValidUntilTimer()

  const {
    challenge,
    resendCode,
    resendError,
    resendFetching,
    resetChallenge,
    solveChallenge,
    solveError,
    solveFetching
  } = useChallengeRequest(wallet)

  const { challengeId, sentTo, twoFactorType, otpLength } = challenge || {}

  const codeSize = useMemo(() => (otpLength > 0 ? otpLength : globalSettings.otpCodeLength), [otpLength])

  const handleChange = (value: string) => {
    const newValue = checkSingleValue(value, { validation: 'numeric' })
    setVerificationCode(newValue)
  }

  const handleResend = () => {
    setVerificationCode('')
    resendCode(challengeId, () => {
      setAttempts(0)
    })
  }

  useEffect(() => {
    if (verificationCode.length === codeSize && attempts < globalSettings.otpMaxAttempts) {
      setAttempts(attempts + 1)
      solveChallenge(challengeId, verificationCode, onSuccess)
    }
  }, [verificationCode])

  useEffect(() => {
    return () => {
      resetChallenge()
    }
  }, [])

  return (
    <div className='payment-container'>
      <div className='otp-form-wrapper vertically-centered' data-test='wallet-checkout-otp'>
        <div className='otp-title'>{getOptTitle(twoFactorType)}</div>
        <div className='otp-subtitle'>
          {twoFactorType === TwoFactorTypes.TOTP
            ? tr('CHECKOUT.OTP.ENTER_CODE', 'Please enter the verification code')
            : tr('CHECKOUT.OTP.ENTER_CODE_TO', "Please enter the verification code that we've sent to")}
          : {twoFactorType !== TwoFactorTypes.TOTP && <span>{sentTo}</span>}
        </div>

        <ErrorDisplay error={solveError} />

        {resendFetching ? null : resendError ? (
          <>
            <ErrorDisplay error={resendError} />

            {onCancel && (
              <MMButton
                type='button'
                variant='primary'
                caption={tr('CHECKOUT.BUTTONS.CANCEL', 'Cancel')}
                onClick={onCancel}
                data-test='button-cancel'
                fullWidth
              />
            )}
          </>
        ) : (
          <>
            <div className='text-center'>
              <input
                id='otp'
                type='tel'
                className={cn('input', {
                  complete: verificationCode?.length === codeSize,
                  wrong: verificationCode?.length === codeSize && solveError != null
                })}
                autoComplete='off'
                onChange={e => handleChange(e.target.value)}
                value={verificationCode}
                maxLength={codeSize}
                autoFocus
                placeholder={tr('FRONTEND.VERIFY_OTP.ENTER_CODE', 'Enter Code')}
                disabled={timer.expired || attempts >= globalSettings.otpMaxAttempts}
                data-test='tel-input'
              />
            </div>

            {twoFactorType !== TwoFactorTypes.TOTP && (
              <div className='resend-message'>
                {attempts < globalSettings.otpMaxAttempts ? (
                  <>
                    {tr('CHECKOUT.OTP.HAVE_NOT_RECEIVED', "Haven't received it?")}
                    <span onClick={handleResend}>{tr('CHECKOUT.OTP.RESEND_CODE', 'Re-send code')}</span>
                  </>
                ) : (
                  tr('CHECKOUT.OTP.NO_MORE_ATTEMPTS', 'No more verification attempts.')
                )}
              </div>
            )}

            <div className='mt-3'>
              {onCancel && (
                <MMButton
                  type='button'
                  variant='primary'
                  caption={tr('CHECKOUT.BUTTONS.CANCEL', 'Cancel')}
                  onClick={onCancel}
                  disabled={solveFetching}
                  data-test='button-cancel'
                  fullWidth
                />
              )}
            </div>
          </>
        )}

        {attempts < globalSettings.otpMaxAttempts && !resendFetching && (
          <div className={cn('timer-wrapper', { expired: timer.expired })}>{timer.formattedTime}</div>
        )}
      </div>
    </div>
  )
}

export default WalletOtp
